/**
 * Meldungen — der Draht zur Wäscherei, jetzt im Leitstand (Phase B).
 *
 * Rahmen und Kopf tragen den Leitstand; der Chat-Kern darunter (Verlauf,
 * Bilder mit Einfügen aus der Zwischenablage, Debug-Schnappschüsse,
 * Status-Wechsel) ist der bewährte Stand und bleibt bewusst unangetastet —
 * die hellen Karten sind die Zettel auf der dunklen Werkbank.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import ImageLightbox from '../../components/ImageLightbox';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import { Kennzahl } from '../../components/leitstand';
import {
  MessageSquare,
  Send,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Check,
  XCircle,
  HelpCircle,
  Clock,
  X,
  Bug,
  Globe,
  Monitor,
  AlertTriangle,
  Info,
} from 'lucide-react';
import {
  getAllFeedbacks,
  updateFeedbackStatus,
  addFeedbackComment,
  getFeedbackImageUrl,
  type Feedback,
  type FeedbackComment,
  type FeedbackCategory,
  type FeedbackStatus,
} from '../../api/client';

const CATEGORY_OPTIONS: { value: FeedbackCategory; label: string }[] = [
  { value: 'BUG', label: 'Fehler' },
  { value: 'FEATURE', label: 'Wunsch' },
  { value: 'OTHER', label: 'Sonstiges' },
];

const STATUS_OPTIONS: { value: FeedbackStatus; label: string }[] = [
  { value: 'OPEN', label: 'Offen' },
  { value: 'RUCKFRAGE', label: 'Rueckfrage' },
  { value: 'DONE', label: 'Erledigt' },
  { value: 'REJECTED', label: 'Abgelehnt' },
];

const STATUS_CONFIG: Record<
  FeedbackStatus,
  { label: string; icon: typeof Check; color: string; bgColor: string }
> = {
  OPEN: { label: 'Offen', icon: Clock, color: 'text-akzent', bgColor: 'bg-akzent-tief' },
  RUCKFRAGE: {
    label: 'Rueckfrage',
    icon: HelpCircle,
    color: 'text-stand',
    bgColor: 'bg-flaeche-2',
  },
  DONE: { label: 'Erledigt', icon: Check, color: 'text-lauf', bgColor: 'bg-flaeche-2' },
  REJECTED: { label: 'Abgelehnt', icon: XCircle, color: 'text-tinte-3', bgColor: 'bg-flaeche-2' },
};

export default function FeedbackPage() {
  const { user } = useAuth();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<FeedbackStatus | ''>('');
  const [filterCategory, setFilterCategory] = useState<FeedbackCategory | ''>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Comment state
  const [commentText, setCommentText] = useState<Record<string, string>>({});
  const [commentImage, setCommentImage] = useState<Record<string, File | null>>({});
  const [commentSubmitting, setCommentSubmitting] = useState<Record<string, boolean>>({});

  const isSystem = user?.role === 'SYSTEM';

  const loadFeedbacks = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filterStatus) params.status = filterStatus;
      if (filterCategory) params.category = filterCategory;
      const data = await getAllFeedbacks(params);
      setFeedbacks(data);
    } catch (e) {
      console.error('Failed to load feedbacks:', e);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCategory]);

  useEffect(() => {
    loadFeedbacks();
  }, [loadFeedbacks]);

  const handleStatusChange = async (feedbackId: string, newStatus: FeedbackStatus) => {
    try {
      await updateFeedbackStatus(feedbackId, newStatus);
      loadFeedbacks();
    } catch (e) {
      console.error('Status update failed:', e);
    }
  };

  const handleAddComment = async (feedbackId: string) => {
    const message = commentText[feedbackId]?.trim();
    const image = commentImage[feedbackId];
    if (!message && !image) return;

    setCommentSubmitting((prev) => ({ ...prev, [feedbackId]: true }));
    try {
      await addFeedbackComment(feedbackId, { message, image: image || undefined });
      setCommentText((prev) => ({ ...prev, [feedbackId]: '' }));
      setCommentImage((prev) => ({ ...prev, [feedbackId]: null }));
      loadFeedbacks();
    } catch (e) {
      console.error('Comment failed:', e);
    } finally {
      setCommentSubmitting((prev) => ({ ...prev, [feedbackId]: false }));
    }
  };

  const handlePaste = (feedbackId: string, e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) {
          setCommentImage((prev) => ({ ...prev, [feedbackId]: file }));
          e.preventDefault();
          break;
        }
      }
    }
  };

  // Stats
  const openCount = feedbacks.filter((f) => f.status === 'OPEN').length;
  const ruckfrageCount = feedbacks.filter((f) => f.status === 'RUCKFRAGE').length;

  const eingabeKlasse =
    'rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 text-sm text-tinte';

  return (
    <LeitstandRahmen titel="Meldungen" benutzer={user?.displayName}>
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kennzahl
            marke="Offen"
            wert={String(openCount)}
            tendenz={openCount > 0 ? 'runter' : 'neutral'}
            neben={openCount === 0 ? 'nichts wartet' : undefined}
          />
          <Kennzahl marke="Rückfrage läuft" klein wert={String(ruckfrageCount)} />
          <Kennzahl marke="Im Filter" klein wert={String(feedbacks.length)} />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as FeedbackStatus | '')}
            className={eingabeKlasse}
          >
            <option value="">Alle Status</option>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value as FeedbackCategory | '')}
            className={eingabeKlasse}
          >
            <option value="">Alle Kategorien</option>
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            onClick={loadFeedbacks}
            disabled={loading}
            className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2 hover:border-linie-stark disabled:opacity-50"
          >
            {loading ? 'Lädt …' : 'Aktualisieren'}
          </button>
        </div>

        {/* Feedback List */}
        {loading ? (
          <div className="text-center py-12 text-sm text-tinte-3">Lade Meldungen …</div>
        ) : feedbacks.length === 0 ? (
          <div className="text-center py-12 text-sm text-tinte-3">
            Keine Meldungen im gewählten Filter.
          </div>
        ) : (
          <div className="space-y-3">
            {feedbacks.map((fb) => (
              <FeedbackCard
                key={fb.id}
                feedback={fb}
                isExpanded={expandedId === fb.id}
                onToggle={() => setExpandedId(expandedId === fb.id ? null : fb.id)}
                isSystem={isSystem}
                currentUserId={user?.userId || ''}
                onStatusChange={handleStatusChange}
                commentText={commentText[fb.id] || ''}
                onCommentTextChange={(text) =>
                  setCommentText((prev) => ({ ...prev, [fb.id]: text }))
                }
                commentImage={commentImage[fb.id] || null}
                onCommentImageChange={(file) =>
                  setCommentImage((prev) => ({ ...prev, [fb.id]: file }))
                }
                onPaste={(e) => handlePaste(fb.id, e)}
                onSubmitComment={() => handleAddComment(fb.id)}
                isSubmitting={commentSubmitting[fb.id] || false}
              />
            ))}
          </div>
        )}
      </div>
    </LeitstandRahmen>
  );
}

// ============================================
// FeedbackCard Component
// ============================================

function FeedbackCard({
  feedback,
  isExpanded,
  onToggle,
  isSystem,
  currentUserId,
  onStatusChange,
  commentText,
  onCommentTextChange,
  commentImage,
  onCommentImageChange,
  onPaste,
  onSubmitComment,
  isSubmitting,
}: {
  feedback: Feedback;
  isExpanded: boolean;
  onToggle: () => void;
  isSystem: boolean;
  currentUserId: string;
  onStatusChange: (feedbackId: string, status: FeedbackStatus) => void;
  commentText: string;
  onCommentTextChange: (text: string) => void;
  commentImage: File | null;
  onCommentImageChange: (file: File | null) => void;
  onPaste: (e: React.ClipboardEvent) => void;
  onSubmitComment: () => void;
  isSubmitting: boolean;
}) {
  const statusCfg = STATUS_CONFIG[feedback.status];
  const StatusIcon = statusCfg.icon;
  const categoryLabel =
    CATEGORY_OPTIONS.find((o) => o.value === feedback.category)?.label || feedback.category;
  const date = new Date(feedback.createdAt);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const submitterName = feedback.submittedBy?.displayName || 'Unbekannt';
  const stationName = feedback.station?.name || feedback.station?.type || null;
  const messageCount = feedback.comments.filter((c) => c.type === 'MESSAGE').length;
  const debugComments = feedback.comments.filter((c) => c.type === 'DEBUG_DATA');

  // Beteiligte: Ersteller + Kommentar-Autoren (unique)
  const participants = (() => {
    const names = new Map<string, string>();
    if (feedback.submittedBy) names.set(feedback.submittedBy.id, feedback.submittedBy.displayName);
    for (const c of feedback.comments) {
      if (c.author && !names.has(c.author.id)) names.set(c.author.id, c.author.displayName);
    }
    return Array.from(names.values());
  })();

  return (
    <div className="rounded-leitstand border border-linie bg-flaeche">
      {/* Header */}
      <button onClick={onToggle} className="w-full text-left px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-zahl text-xs text-tinte-3">#{feedback.number}</span>
              <div
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${statusCfg.color}`}
              >
                <StatusIcon className="w-3 h-3" />
                {statusCfg.label}
              </div>
              <span className="text-xs px-1.5 py-0.5 rounded bg-flaeche-2 text-tinte-2">
                {categoryLabel}
              </span>
              {debugComments.length > 0 && (
                <span
                  className="flex items-center gap-0.5 text-[11px] text-stoer"
                  title="Debug-Snapshots vorhanden"
                >
                  <Bug className="w-3 h-3" />
                  {debugComments.length}
                </span>
              )}
            </div>
            <p className="text-sm font-semibold text-tinte line-clamp-2">{feedback.title}</p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-xs text-tinte-3">
              <span className="font-medium text-tinte-2">{participants.join(', ')}</span>
              {stationName && (
                <>
                  <span className="text-tinte-3">&middot;</span>
                  <span>Station: {stationName}</span>
                </>
              )}
              <span className="text-tinte-3">&middot;</span>
              <span>
                {date.toLocaleDateString('de-DE')}{' '}
                {date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
              </span>
              {messageCount > 0 && (
                <>
                  <span className="text-tinte-3">&middot;</span>
                  <span className="flex items-center gap-0.5">
                    <MessageSquare className="w-3 h-3" />
                    {messageCount} Nachricht{messageCount !== 1 ? 'en' : ''}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="mt-1 shrink-0">
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-tinte-3" />
            ) : (
              <ChevronDown className="w-4 h-4 text-tinte-3" />
            )}
          </div>
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-linie px-4 py-3 space-y-3">
          {/* Header mit Status-Dropdown */}
          <div className="flex items-center justify-between">
            <div className="text-xs text-tinte-3">
              {feedback.submittedBy?.displayName || 'Unbekannt'}
              {stationName ? ` · ${stationName}` : ''}
              {feedback.pageUrl ? ` · ${feedback.pageUrl}` : ''}
            </div>
            {isSystem ? (
              <select
                value={feedback.status}
                onChange={(e) => onStatusChange(feedback.id, e.target.value as FeedbackStatus)}
                className={`rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-1 text-sm font-medium cursor-pointer ${statusCfg.color}`}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <div
                className={`text-xs font-medium px-2 py-0.5 rounded ${statusCfg.color} ${statusCfg.bgColor}`}
              >
                {statusCfg.label}
              </div>
            )}
          </div>

          {/* --- CHAT CONTAINER START --- */}
          <div className="flex flex-col gap-3 p-4 bg-grund border border-linie rounded-leitstand h-[600px] overflow-y-auto">
            {feedback.comments.map((comment) => {
              // 1. SYSTEM EVENTS
              if (comment.type === 'SYSTEM_EVENT') {
                return (
                  <div key={comment.id} className="flex justify-center my-2">
                    <span className="text-xs text-tinte-3 font-medium bg-flaeche-2 px-3 py-1 rounded-full">
                      — {comment.message} —
                    </span>
                  </div>
                );
              }

              // 2. DEBUG DATA (Rot und Auffaellig)
              if (comment.type === 'DEBUG_DATA') {
                let debugContent = 'Fehlerhafte Daten';
                try {
                  debugContent = JSON.stringify(JSON.parse(comment.metadata || '{}'), null, 2);
                } catch (e) {
                  /* ignore */
                }

                return (
                  <details
                    key={comment.id}
                    className="group border border-stoer rounded-md mb-4 overflow-hidden"
                  >
                    <summary className="cursor-pointer p-2 text-xs font-bold text-stoer hover:bg-flaeche-2 flex items-center gap-2">
                      <Bug className="w-4 h-4" />
                      <span>TECH-SNAPSHOT / DEBUG DATEN</span>
                      <span className="font-normal text-tinte-3">(Klicken zum Oeffnen)</span>
                    </summary>
                    <pre className="p-3 text-[10px] leading-tight overflow-x-auto bg-grund border-t border-linie text-tinte-2 max-h-48 overflow-y-auto">
                      {debugContent}
                    </pre>
                  </details>
                );
              }

              // 3. MESSAGES (Chat Style)
              const isMe = comment.authorId === currentUserId;

              return (
                <div
                  key={comment.id}
                  className={`flex w-full mb-4 ${isMe ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-4 shadow-sm relative text-sm ${
                      isMe
                        ? 'bg-akzent-tief border border-linie-2 rounded-2xl rounded-tr-sm'
                        : 'bg-flaeche-2 border border-linie-2 rounded-2xl rounded-tl-sm'
                    }`}
                  >
                    {/* Header: Name und Zeit */}
                    <div className="flex justify-between items-baseline gap-4 mb-2 border-b border-linie pb-1">
                      <span className={`font-semibold ${isMe ? 'text-akzent' : 'text-tinte'}`}>
                        {comment.author?.displayName || 'Unbekannt'}
                      </span>
                      <span className="text-[10px] opacity-60">
                        {new Date(comment.createdAt).toLocaleString('de-DE')}
                      </span>
                    </div>

                    {/* Nachricht */}
                    {comment.message && (
                      <div className="whitespace-pre-wrap break-words text-tinte leading-relaxed">
                        {comment.message}
                      </div>
                    )}

                    {/* Bild */}
                    {comment.imagePath && (
                      <div className="mt-3">
                        <img
                          src={getFeedbackImageUrl(comment.imagePath)}
                          alt="Attachment"
                          className="rounded border border-linie max-h-64 object-contain bg-flaeche-2 cursor-pointer hover:opacity-90"
                          onClick={() => setLightboxSrc(getFeedbackImageUrl(comment.imagePath!))}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* --- CHAT CONTAINER END --- */}

          {/* Antwort schreiben */}
          <div className="space-y-2">
            <div className="relative">
              <textarea
                value={commentText}
                onChange={(e) => onCommentTextChange(e.target.value)}
                onPaste={onPaste}
                placeholder="Antwort schreiben..."
                rows={2}
                className="w-full rounded-[4px] border border-linie-2 bg-flaeche px-3 py-2 text-sm text-tinte resize-none focus:outline-none focus:border-akzent pr-10"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute right-2 bottom-2 text-tinte-3 hover:text-tinte-2"
                title="Bild hinzufuegen"
              >
                <ImageIcon className="w-4 h-4" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  onCommentImageChange(file);
                  e.target.value = '';
                }}
              />
            </div>

            {commentImage && (
              <div className="flex items-center gap-2 text-xs text-tinte-2">
                <ImageIcon className="w-3 h-3" />
                <span className="truncate">{commentImage.name || 'Screenshot'}</span>
                <button onClick={() => onCommentImageChange(null)} className="text-stoer">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            <button
              onClick={onSubmitComment}
              disabled={isSubmitting || (!commentText.trim() && !commentImage)}
              className="flex items-center gap-1.5 rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-3 h-3" />
              {isSubmitting ? 'Senden...' : 'Antwort senden'}
            </button>
          </div>
        </div>
      )}

      {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}

// ============================================
// Debug Data Panel
// ============================================

interface DebugSnapshot {
  consoleLogs?: { type: string; args: string[]; timestamp: number }[];
  networkErrors?: {
    method: string;
    url: string;
    status: number | null;
    error?: string;
    timestamp: number;
  }[];
  systemInfo?: {
    userAgent?: string;
    viewport?: string;
    screen?: string;
    language?: string;
    url?: string;
    referrer?: string;
  };
  timestamp?: string;
  uptimeMs?: number;
}

function DebugDataPanel({ comment }: { comment: FeedbackComment }) {
  let data: DebugSnapshot | null = null;
  try {
    data = comment.metadata ? JSON.parse(comment.metadata) : null;
  } catch {
    return null;
  }
  if (!data) return null;

  const logTypeColors: Record<string, string> = {
    error: 'text-stoer',
    warn: 'text-stand',
    log: 'text-tinte-3',
  };

  const logTypeIcons: Record<string, typeof AlertTriangle> = {
    error: XCircle,
    warn: AlertTriangle,
    log: Info,
  };

  return (
    <details className="border border-linie-2 bg-flaeche-2 rounded-lg overflow-hidden">
      <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-flaeche transition-colors text-sm">
        <Bug className="w-4 h-4 text-stoer shrink-0" />
        <span className="font-medium text-tinte">Tech-Snapshot</span>
        <span className="font-zahl text-[11px] text-tinte-3 ml-auto">
          {new Date(comment.createdAt).toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </summary>
      <div className="border-t border-linie-2 px-3 py-2.5 space-y-3">
        {/* System Info */}
        {data.systemInfo && (
          <div className="space-y-1">
            <h4 className="text-xs font-semibold text-tinte-2 uppercase tracking-wide">System</h4>
            <div className="grid grid-cols-2 gap-1 text-xs">
              {data.systemInfo.url && (
                <div className="col-span-2 flex items-center gap-1.5 text-tinte-2">
                  <Globe className="w-3 h-3 text-tinte-3" />
                  <span className="font-mono truncate">{data.systemInfo.url}</span>
                </div>
              )}
              {data.systemInfo.viewport && (
                <div className="flex items-center gap-1.5 text-tinte-2">
                  <Monitor className="w-3 h-3 text-tinte-3" />
                  Viewport: {data.systemInfo.viewport}
                </div>
              )}
              {data.systemInfo.screen && (
                <div className="text-tinte-2">Screen: {data.systemInfo.screen}</div>
              )}
              {data.systemInfo.userAgent && (
                <div className="col-span-2 text-tinte-3 truncate text-[11px] font-mono">
                  {data.systemInfo.userAgent}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Console Logs */}
        {data.consoleLogs && data.consoleLogs.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-tinte-2 uppercase tracking-wide mb-1">
              Console ({data.consoleLogs.length})
            </h4>
            <div className="bg-grund rounded-lg p-2 max-h-[200px] overflow-y-auto">
              {data.consoleLogs.map((log, i) => {
                const LogIcon = logTypeIcons[log.type] || Info;
                return (
                  <div
                    key={i}
                    className={`flex items-start gap-1.5 text-[11px] font-mono leading-relaxed ${logTypeColors[log.type] || 'text-tinte-3'}`}
                  >
                    <LogIcon className="w-3 h-3 shrink-0 mt-0.5" />
                    <span className="break-all">{log.args.join(' ')}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Network Errors */}
        {data.networkErrors && data.networkErrors.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-stoer uppercase tracking-wide mb-1">
              Network Errors ({data.networkErrors.length})
            </h4>
            <div className="bg-grund rounded-lg p-2 max-h-[150px] overflow-y-auto">
              {data.networkErrors.map((err, i) => (
                <div key={i} className="text-[11px] font-mono text-tinte-2 leading-relaxed">
                  <span className="text-stoer font-bold">{err.method}</span>{' '}
                  <span className="text-tinte-2">{err.url}</span>{' '}
                  <span className="text-stoer">{err.status ?? 'ERR'}</span>
                  {err.error && <span className="text-stoer ml-1">({err.error})</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Uptime */}
        {data.uptimeMs !== undefined && (
          <p className="text-[11px] text-tinte-3">
            Uptime: {Math.round(data.uptimeMs / 1000)}s
            {data.timestamp && (
              <span className="ml-2">
                Snapshot: {new Date(data.timestamp).toLocaleTimeString('de-DE')}
              </span>
            )}
          </p>
        )}
      </div>
    </details>
  );
}
